import React from 'react';
import { getDisplayCurrency, getLocalPrice } from '../../utils/displayCurrency';
import { Check, Minus } from 'lucide-react';

const YES = 'yes';
const NO = 'no';

const ROWS = [
  { group: 'Your AvA Team' },
  { label: 'Maya — Your Guide', cells: [YES, YES, YES, YES] },
  { label: 'AvAs to practise with (First Steps: Rebecca, Chloe & Marcus)', cells: ['3 of 9', '6 of 9', 'All 9', 'All 9'] },
  { label: 'Neo — Your Friend (customise name, voice, image, and backstory)', cells: [NO, YES, YES, YES] },
  { group: 'Challenges & Rewards' },
  { label: 'Challenges 1–10 — virtual in-app rewards (badges)', cells: [NO, YES, YES, YES] },
  { label: 'Challenges 11–20 — AI-generated rewards, locked in a 180-Day Vault', cells: [NO, NO, YES, YES] },
  { label: 'Parent-authorised minting via the Parent Dashboard', cells: [NO, NO, YES, YES] },
  { label: 'Early access to new challenges', cells: [NO, NO, NO, YES] },
  { group: 'Practice Time' },
  { label: 'Practice conversations (text)', cells: [YES, 'Unlimited', 'Unlimited', 'Unlimited'] },
  { label: 'Voice minutes / month', cells: ['10 free mins', '45', '180', '300'] },
  { label: 'Pay-as-you-go Hearts top-ups (First Steps: teen\u2019s own card — challenges earn virtual badges only)', cells: [YES, YES, YES, YES] },
  { group: 'Family' },
  { label: 'Parent Dashboard + progress + shared access', cells: [NO, NO, YES, YES] },
  { label: 'Extra teen 40% discount, all on one bill', cells: [NO, NO, NO, YES] },
  { group: 'Privacy — every plan' },
  { label: 'Teen controls their conversations', cells: [YES, YES, YES, YES] },
  { label: 'Delete anytime · parents can\u2019t view chats', cells: [YES, YES, YES, YES] },
  { label: '~90-day auto-clear of conversations', cells: [YES, YES, YES, YES] },
  { group: 'Guarantees' },
  { label: '30-day refund + 90-day pause + indefinite pause', cells: [NO, YES, YES, YES] },
  { label: 'Priority support', cells: [NO, NO, NO, YES] },
];

const HEADERS = [
  { name: 'First Steps', price: `${getDisplayCurrency().symbol}0`, color: '#059669', bg: '#D1FAE5' },
  { name: 'Steady', price: `${getLocalPrice('steady').text}/mo`, color: '#7B4FD8', bg: '#EDE9FE' },
  { name: 'Together', price: `${getLocalPrice('together').text}/mo`, color: '#FFFFFF', bg: '#7B4FD8' },
  { name: 'Brave ★', price: `${getLocalPrice('brave').text}/yr`, color: '#FFFFFF', bg: '#F5831F' },
];

const Cell = ({ value, colIdx }) => {
  const braveTint = colIdx === 3 ? { backgroundColor: 'rgba(245,131,31,0.07)' } : {};
  let content;
  if (value === YES) {
    content = (
      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full" style={{ backgroundColor: '#DCFCE7' }}>
        <Check size={15} strokeWidth={3.5} style={{ color: '#16A34A' }} />
      </span>
    );
  } else if (value === NO) {
    content = <Minus size={15} strokeWidth={2.5} className="text-gray-300 mx-auto" />;
  } else {
    const strong = value !== '—';
    content = (
      <span className={`text-sm font-extrabold ${strong ? '' : 'text-gray-300'}`} style={strong ? { color: HEADERS[colIdx].bg === '#7B4FD8' || HEADERS[colIdx].bg === '#F5831F' ? HEADERS[colIdx].bg : HEADERS[colIdx].color } : {}}>
        {value}
      </span>
    );
  }
  return <td className="px-3 py-3 text-center" style={braveTint}>{content}</td>;
};

export const PlanComparisonTable = () => (
  <div className="mt-14 max-w-4xl mx-auto" data-testid="plan-comparison-table">
    <h3 className="text-2xl font-bold text-gray-900 text-center mb-6" style={{ fontFamily: 'Georgia, serif' }}>
      Compare every plan
    </h3>
    <p className="text-center text-[11px] text-gray-400 mb-2 md:hidden" data-testid="comparison-swipe-hint">Swipe to see all plans →</p>
    <div className="relative rounded-2xl border-2 shadow-lg bg-white" style={{ borderColor: '#F5831F55' }}>
      <div className="overflow-x-auto rounded-2xl">
      <table className="w-full text-sm min-w-[640px]">
        <thead>
          <tr className="border-b-2" style={{ borderColor: '#1A1209' }}>
            <th className="text-left px-4 py-4 font-bold text-gray-700 w-[40%] align-middle">Feature</th>
            {HEADERS.map((h, i) => (
              <th key={h.name} className="px-2 py-3 text-center align-middle" style={i === 3 ? { backgroundColor: 'rgba(245,131,31,0.07)' } : {}}>
                <span className="inline-block px-4 py-1.5 rounded-full font-extrabold text-sm tracking-wide shadow-sm" style={{ backgroundColor: h.bg, color: h.color }}>
                  {h.name}
                </span>
                <span className="block text-xs font-bold mt-1.5 text-gray-700">{h.price}</span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map((row, i) => row.group ? (
            <tr key={row.group} style={{ background: 'linear-gradient(90deg, #F5831F22, #F5831F0A)' }}>
              <td colSpan={5} className="px-4 py-2.5 text-xs font-extrabold uppercase tracking-[0.15em]" style={{ color: '#C2410C' }}>{row.group}</td>
            </tr>
          ) : (
            <tr key={row.label} className={`border-b border-gray-100 ${i % 2 ? 'bg-[#FAF7F2]' : 'bg-white'}`}>
              <td className="px-4 py-3 font-medium text-gray-800">{row.label}</td>
              {row.cells.map((c, j) => (
                <Cell key={j} value={c} colIdx={j} />
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="md:hidden pointer-events-none absolute inset-y-0 right-0 w-8 rounded-r-2xl" style={{ background: 'linear-gradient(to left, rgba(255,255,255,0.95), rgba(255,255,255,0))' }} aria-hidden="true" />
    </div>
    <p className="text-xs text-gray-500 text-center mt-3 max-w-2xl mx-auto" data-testid="vault-governance-note">
      Challenges 1–10 earn virtual in-app rewards. Challenges 11–20 earn custom AI-generated rewards, safely locked in a 180-Day Vault — minting always requires parent authorisation via the Parent Dashboard.
    </p>
  </div>
);

export default PlanComparisonTable;
